/**
 * optionsim.service — the BTC options simulator API (/v1/optionsim). Read-only with respect to
 * trading: runs are queued and executed by a forked worker on the server; nothing here can
 * place an order.
 */
import api from './api';

export interface SimParamSpec {
  key: string; label: string; type: 'number' | 'boolean' | 'select' | 'json'; default: any;
  min?: number; max?: number; step?: number; options?: Array<{ value: string; label: string }>; help?: string; nullable?: boolean;
}
export interface SimStrategyInfo { id: string; label: string; description: string; usesEngine: boolean; params: SimParamSpec[] }
export interface SimCatalog {
  strategies: SimStrategyInfo[];
  usdInr: number;
  defaults: { contractsPerLeg: number; capitalInr: number; marginMode: 'CROSS' | 'PM'; costScenarios: Array<'base' | 'stressed'> };
}

export interface SimCoverage {
  ready: boolean;
  note?: string;
  contracts?: { n: number; first: string | null; last: string | null };
  expiries?: Array<{ t: number; contracts: number; withBars: number }>;
  index?: { days: number; first: string | null; last: string | null };
  quoteSnaps?: { n: number; first: string | null; last: string | null };
  sizeMb?: { bars: number; snaps: number };
  jobs?: { collectorEnabled: boolean; quoteSamplerEnabled: boolean } | null;
  runner?: { disabled: boolean; busy: boolean };
}

export type SimRunStatus = 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';

export interface SimMetrics {
  n: number; totalNetUsd: number; totalGrossUsd: number; feesUsd: number; slippageUsd: number;
  meanUsd: number; medianUsd: number; meanCi95: [number, number] | null; winRatePct: number;
  avgWinUsd: number; avgLossUsd: number; largestWinUsd: number; largestLossUsd: number; profitFactor: number | null;
  es95Usd: number; es99Usd: number; maxDrawdownUsd: number; worstCycleMtmUsd: number; adjustmentsPerCycle: number;
  avgAdjustmentCostUsd: number | null; stops: number; recoveredPct: number | null; avgMaxMarginUtilPct: number | null;
  maxMarginUtilPct: number | null; marginBreachCyclePct: number; returnOnCapitalPct: number; returnOnMaxImPct: number | null;
  /** Cycles where the equity (capital + P&L) was ≤ 0: no liquidation is modelled, the run kept trading. */
  capitalExhaustedCycles?: number; firstExhaustedCycle?: number | null;
  totalExclBestUsd: number; totalExclTwoBestUsd: number;
}

export interface SimSubRun {
  key: string; strategyId: string; label: string; params: Record<string, any>; costScenario: 'base' | 'stressed';
  metrics: SimMetrics; regimes: Record<string, { n: number; meanUsd: number; worstUsd: number; totalUsd: number }>;
  cycles: number; stats: { instants: number; fills: number; decisions: number; skippedEntries: number; cancelled: boolean };
  execVersion: { spread: string; depth: string; costScenario: string; compat: string | null; marginMode: string };
  dataNote: string | null;
}

export interface SimAltRow {
  id: string; letter: string; label: string; feasible: boolean; why: string | null; costUsd: number; evUsd: number;
  es99Usd: number; stressUsd: number; reversalUsd: number | null; delta: number; gammaPer1Pct: number; vega: number; theta: number;
  imUsd: number; utilPct: number | null; dRisk: number; giveUp: number; score: number; breaches: string[]; liquidity: string;
}

export interface SimGreeks { delta: number; gamma: number; gammaPer1Pct: number; vega: number; theta: number }

export interface SimAnalyseSummary {
  kind: 'analyse'; asOf: number; spot: number; usdInr: number; capitalInr: number; pnlUsd: number; pnlKnown: boolean; indexHistory: string;
  legs: Array<{ symbol: string; qty: number; mark: number; iv: number | null; delta: number; entryPx: number | null }>;
  greeksBefore: SimGreeks;
  margin: { mode: 'CROSS' | 'PM'; crossImUsd: number; pmImUsd: number; crossLabel: string; pmLabel: string };
  chosen: SimAltRow; reason: string; explanation: string; hold: SimAltRow; closeAll: SimAltRow; alternatives: SimAltRow[];
  candidates: number; scenarioMeta: { method: string; windows: number; draws: number; horizonSec: number; sigmaNowAnnual: number | null };
}

export interface SimRunRow {
  id: number; kind: 'backtest' | 'compare' | 'analyse'; name: string | null; status: SimRunStatus; progressPct: number;
  phase: string | null; error: string | null; createdAt: string; startedAt: string | null; finishedAt: string | null;
  config: any; headline?: any; summary?: { kind: string; runs?: SimSubRun[]; generatedAt?: string } & Partial<SimAnalyseSummary>;
}

export interface SimCycle {
  key: string; cycle: number; basketId: string;
  summary: {
    cycle: number; entryT: number; exitT: number; expiry: number; entrySpot: number; endSpot: number; minPct: number; maxPct: number;
    creditUsd: number; pnlUsd: number; grossPnlUsd: number; feesUsd: number; slippageUsd: number; adjustments: number; adjustmentCostUsd: number;
    stopped: boolean; closeReason: string | null; worstMtmUsd: number; bestMtmUsd: number; maxImUsd: number; maxMarginUtilPct: number | null; marginBreaches: number;
    equityExhausted?: boolean;
  };
}

export interface SimFill { key: string; cycle: number; t: number; symbol: string; kind: string; side: 'BUY' | 'SELL'; qty: number; px: number; mark: number; halfSpread: number; feeUsd: number; slippageUsd: number; levels: number; market: boolean; reason: string; spot: number }
export interface SimDecision { key: string; cycle: number; t: number; spot: number; trigger: string; action: string; greeksBefore: SimGreeks; greeksAfter: SimGreeks; snapBackBeforeUsd?: number; snapBackAfterUsd?: number; netCashUsd?: number; costUsd?: number; counted?: boolean; alternatives?: SimAltRow[]; note?: string }
export interface SimReplay { key: string; cycle: number; summary: SimCycle['summary'] | null; fills: SimFill[]; decisions: SimDecision[]; events: any[]; index: Array<[number, number]> }

const d = (r: any) => r.data?.data;
/** Mutations show their own inline error, so the global toast is suppressed for them. */
const quiet = { headers: { _suppressToast: 'true' } };
/** The server's `{ success: false, error: { message } }`, else the transport error. */
export const simError = (e: any): string => e?.response?.data?.error?.message || e?.message || 'request failed';

export const optionsimService = {
  strategies: () => api.get<any>('/optionsim/strategies').then(r => d(r) as SimCatalog),
  coverage: () => api.get<any>('/optionsim/catalog/coverage').then(r => d(r) as SimCoverage),
  createRun: (body: any) => api.post<any>('/optionsim/runs', body, quiet).then(r => d(r) as { id: number; status: string; name: string }),
  listRuns: () => api.get<any>('/optionsim/runs').then(r => (d(r) || []) as SimRunRow[]),
  getRun: (id: number) => api.get<any>(`/optionsim/runs/${id}`).then(r => d(r) as SimRunRow),
  artifact: <T = any>(id: number, kind: string, params: Record<string, any> = {}) =>
    api.get<any>(`/optionsim/runs/${id}/artifacts/${kind}`, { params }).then(r => d(r) as { part: number; parts: number; items: T[] }),
  replay: (id: number, key: string, cycle: number) => api.get<any>(`/optionsim/runs/${id}/replay`, { params: { key, cycle } }).then(r => d(r) as SimReplay),
  cancel: (id: number) => api.post<any>(`/optionsim/runs/${id}/cancel`, undefined, quiet).then(r => d(r) as { result: string }),
  remove: (id: number) => api.delete<any>(`/optionsim/runs/${id}`, quiet).then(r => r.data),
  /** Rank adjustments for an open basket on the live public chain (read-only: places nothing). */
  analyseBasket: (basketId: number, opts: { capitalInr?: number; marginMode?: 'CROSS' | 'PM' } = {}) =>
    api.post<any>('/optionsim/analyse', { basketId, ...opts }, quiet).then(r => d(r) as { id: number; status: string }),
  exportCsv: (id: number) => api.get<any>(`/optionsim/runs/${id}/export.csv`, { responseType: 'blob' } as any).then(r => r.data as Blob),
};
